<template>
  <div class="row">
    <div class="col">
      <label for="trust-signals">Trust Signals</label>
      <p class="hint">
        Shown under the Add to cart button on this artwork, in the order below —
        a per-artwork signal can sit above a global one. Untick to hide a signal on
        this artwork only. Signals themselves are created and edited under
        Artworks &gt; Trust Signals.
      </p>

      <div class="include-global">
        <el-checkbox v-model="includeGlobal" @change="onIncludeGlobalChange">
          Include global trust signals
        </el-checkbox>
        <p class="hint hint-under">
          On: global signals are listed below, ticked by default, and you can untick
          any of them for this artwork alone. Off: global signals are left out
          entirely and only the per-artwork signals you tick are shown.
        </p>
      </div>

      <p v-if="signals.length === 0" class="empty-notice">
        {{
          includeGlobal
            ? "No trust signals defined yet."
            : "No per-artwork signals defined yet."
        }}
      </p>

      <draggable
        v-else
        :list="signals"
        item-key="id"
        class="signal-list"
        ghost-class="ghost-row"
        handle=".drag-handle"
      >
        <template #item="{ element: row, index: i }">
          <div
            class="signal-row"
            :class="{ 'is-inactive': !row.is_active, 'is-global': row.is_global }"
          >
            <span class="position-num">{{ i + 1 }}</span>
            <span class="drag-handle" title="Drag to reorder">
              <font-awesome-icon :icon="['fas', 'arrows-alt']" />
            </span>
            <el-checkbox v-model="row.is_visible" :disabled="!row.is_active" />
            <img
              v-if="row.icon_url"
              :src="previewDomain + row.icon_url"
              class="signal-icon"
              alt=""
            />
            <span v-else class="signal-icon-empty" title="No icon" />
            <span class="signal-label">{{ row.label }}</span>
            <span v-if="row.link_url" class="badge badge-link" :title="row.link_url">link</span>
            <span v-if="row.is_global" class="badge badge-global">global</span>
            <span v-else class="badge">per artwork</span>
            <span v-if="!row.is_active" class="badge badge-off">hidden everywhere</span>
          </div>
        </template>
      </draggable>

      <el-button class="button_style_1 mt-2" :loading="saving" @click="save">
        Save
      </el-button>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { ElNotification } from "element-plus";

export default {
  name: "TrustSignals",
  components: { draggable },
  props: ["postId"],
  data() {
    return {
      signals: [],
      // Globals pulled out of the list while include_global is off. Kept with the
      // index they sat at, so switching back on restores their position instead of
      // dumping them at the end.
      parkedGlobals: [],
      includeGlobal: true,
      saving: false,
    };
  },
  computed: {
    previewDomain() {
      return process.env.VUE_APP_SHARED_API;
    },
    endpoint() {
      return `${process.env.VUE_APP_SHARED_API}api/artworks/${this.postId}/trust-signals`;
    },
  },
  created() {
    this.load();
  },
  methods: {
    applyState(data) {
      this.includeGlobal = data.include_global;
      const all = data.signals || [];
      this.signals = this.includeGlobal ? all : all.filter((s) => !s.is_global);
      this.parkedGlobals = this.includeGlobal
        ? []
        : all
            .map((signal, index) => ({ index, signal }))
            .filter((entry) => entry.signal.is_global);
    },
    load() {
      this.$api
        .get(this.endpoint)
        .then((res) => {
          this.applyState(res.data);
        })
        .catch(() => {
          ElNotification({
            title: "Error",
            message: "Could not load trust signals.",
            type: "error",
          });
        });
    },
    onIncludeGlobalChange(value) {
      if (value) {
        // Put each global back where it was, clamped to the current length.
        this.parkedGlobals.forEach((entry) => {
          const at = Math.min(entry.index, this.signals.length);
          this.signals.splice(at, 0, entry.signal);
        });
        this.parkedGlobals = [];
      } else {
        this.parkedGlobals = this.signals
          .map((signal, index) => ({ index, signal }))
          .filter((entry) => entry.signal.is_global);
        this.signals = this.signals.filter((s) => !s.is_global);
      }
    },
    save() {
      this.saving = true;
      this.$api
        .post(this.endpoint, {
          include_global: this.includeGlobal ? "1" : "0",
          signals: this.signals.map((s, index) => ({
            id: s.id,
            is_hidden: s.is_visible ? "0" : "1",
            sort_order: index,
          })),
        })
        .then((res) => {
          this.applyState(res.data);
          ElNotification({
            title: "Success!",
            message: "Trust signals saved for this artwork.",
            type: "success",
          });
        })
        .catch(() => {
          ElNotification({
            title: "Error",
            message: "Could not save trust signals.",
            type: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.hint {
  font-size: 12px;
  color: #888;
  margin: 4px 0 10px;
}
.hint-under {
  margin: 2px 0 0 24px;
}
.include-global {
  padding: 10px 0 14px;
  margin-bottom: 4px;
  border-bottom: 1px solid #ebeef5;
}
.empty-notice {
  color: #888;
}
.mt-2 {
  margin-top: 10px;
}
.signal-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.signal-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
}
.signal-row.is-global {
  background: #fafaf8;
}
.signal-row.is-inactive {
  opacity: 0.6;
}
.position-num {
  min-width: 22px;
  font-size: 12px;
  color: #888;
  text-align: center;
  font-weight: 600;
}
.drag-handle {
  flex-shrink: 0;
  cursor: grab;
  color: #909399;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  user-select: none;
}
.drag-handle:active {
  cursor: grabbing;
}
.signal-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}
.signal-icon-empty {
  width: 26px;
  height: 26px;
  border: 1px dashed #dcdfe6;
  flex-shrink: 0;
}
.signal-label {
  flex: 1;
  font-size: 13px;
  min-width: 0;
}
.badge {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border: 1px solid #dcdfe6;
  color: #909399;
  flex-shrink: 0;
}
.badge-link {
  border-color: #1f3c2a;
  color: #1f3c2a;
}
.badge-global {
  border-color: #1f3c2a;
  color: #1f3c2a;
  background: #f0f4f1;
}
.badge-off {
  border-color: #e53e3e;
  color: #e53e3e;
}
.ghost-row {
  opacity: 0.5;
}
</style>
